import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { FormControl, Button, SelectChangeEvent } from "@mui/material";
import SingleSelect from "../../../../components/commonComponent/SingleSelect";
import MultiSelectNew from "../../../../components/MultiSelectNew";
import { IconArrowRight } from "../../../../icons/Icon";
import {
  fetchAllRegions,
  fetchAllSites,
  fetchSectors,
} from "../../../../services/sectorRegionAllSitesAdminConfigurationSlice";
import { setadminConfigurationParams } from "../../../../services/adminConfigurationParamsSlice";
import { AppDispatch, RootState } from "../../../../store/store";
import styles from "./filterdata.module.scss";

export interface Sector {
  sectorname: string;
  sectorcode: string;
}

export interface Region {
  regionid: string;
  regionname: string;
}

export interface Site {
  id: number;
  sitename: string;
  siteid: string;
  sitecode: string;
}

interface SelectedOptions {
  sector: string;
  region: string;
}

interface FilterDataProps {
  handleClick: () => void;
  handleMenuChange: (val: boolean) => void;
}

const FilterData: React.FC<FilterDataProps> = ({ handleClick, handleMenuChange }) => {
  const dispatch = useDispatch<AppDispatch>();
  const userId = localStorage.getItem("userid");

  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
  const { sectors, regions, sites } = useSelector<RootState, any>(
    (state) => state.sectorRegionAllSitesAdminConfiguration,
    shallowEqual
  );

  const [selectedSiteOptions, setSelectedSiteOptions] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({
    sector: "",
    region: "",
  });

  // Memoized site options

  const siteOptions = useMemo(() => {
    if (!sites || sites.length === 0) return [];
    return sites.map((site: any) => ({
      id: site.sitecode,
      label: site.sitename,
      code: site.sitecode
    }));
  }, [sites]);

  useEffect(() => {
    dispatch(fetchSectors(userId));
  }, []);

  useEffect(() => {
    setButtonDisabled(true);
    if (sites.length > 0) {
      setSelectedSiteOptions([]);
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [sites]);

  const sectorOptions = useMemo(
    () => sectors.map((s: Sector) => ({ label: s.sectorname, value: s.sectorcode })),
    [sectors]
  );

  const regionOptions = useMemo(
    () => regions.map((r: Region) => ({ label: r.regionname, value: r.regionid })),
    [regions]
  );


  // FIXED: Handling "All Sites" selection correctly
  const handleChangeSite = useCallback(
    (selected: string[]) => {
      setSelectedSiteOptions(selected);
    },
    [siteOptions]
  );

  const handleChangeSector = useCallback(
    (event: SelectChangeEvent<string | number>) => {
      setButtonDisabled(true);
      const value = event.target.value as string;
      setSelectedOptions((prev) => ({ ...prev, sector: value, region: "" }));
      dispatch(fetchAllRegions({ userId, sectorCode: value }));
      setSelectedSiteOptions([]);
    },
    [dispatch, userId]
  );

  const handleChangeRegion = useCallback(
    (event: SelectChangeEvent<string | number>) => {
      setButtonDisabled(true);
      const value = event.target.value as string;
      setSelectedOptions((prev) => ({ ...prev, region: value }));
      dispatch(fetchAllSites({ userId, sector: selectedOptions.sector, region: value }));
      setSelectedSiteOptions([]);
    },
    [dispatch, selectedOptions.sector, userId]
  );


  const allSiteCodes = useMemo(() => {
    if (!sites || sites.length === 0) return [];
    return sites.map((site: Site) => site.sitecode);
  }, [sites]);


  const isAllSitesSelected = useMemo(() => {
    return selectedSiteOptions.length !== 0 && selectedSiteOptions.length === siteOptions.length;
  }, [selectedSiteOptions, siteOptions]);


  const handleSubmit = useCallback(() => {
    let finalSiteList = [];

    if (selectedSiteOptions.length === 0 && sites.length > 0) {
      // Case 1: No sites selected, but sites exist => Send all sites
      finalSiteList = allSiteCodes;
    } else if (isAllSitesSelected) {
      // Case 2: "All Sites" is selected => Send all sites
      finalSiteList = allSiteCodes;
    } else {
      // Case 3: Some sites selected => Send selected sites only
      finalSiteList = selectedSiteOptions;
    }

    const payload = {
      userid: userId,
      sitelist: finalSiteList,
      isallsite: isAllSitesSelected || selectedSiteOptions.length === 0,
      regionid: Number(selectedOptions.region),
      sector: selectedOptions.sector,
    };

    dispatch(setadminConfigurationParams(payload));
    handleClick();
    handleMenuChange(true);
  }, [
    selectedOptions,
    selectedSiteOptions,
    sites,
    allSiteCodes,
    dispatch,
    handleClick,
    handleMenuChange,
    userId,
    siteOptions,
  ]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl fullWidth>
        <div className="flex justify-content-between align-items-end">
          <div className={`${styles.leftBox} flex justify-content-between align-items-end`}>
            <div className={`flex flex-column ${styles.multiSelect}`}>
              <SingleSelect
                label="Select Sector"
                options={sectorOptions}
                value={selectedOptions.sector}
                onChange={handleChangeSector}
                placeholder="Select Sector"
                disabled={sectorOptions.length < 2}
              />
            </div>

            <div className={`flex flex-column ${styles.multiSelect}`}>
              <SingleSelect
                label="Region"
                options={regionOptions}
                value={selectedOptions.region}
                onChange={handleChangeRegion}
                placeholder="Select Region"
                disabled={!selectedOptions.sector || regionOptions.length < 2}
              />
            </div>

            <div className={`flex flex-column ${styles.multiSelect}`}>
              <MultiSelectNew
                options={siteOptions}
                label="Select Site"
                disabled={siteOptions.length < 2}
                selectedValues={selectedSiteOptions}
                onApply={handleChangeSite}
                placeholder="Select Site"
                selectAllLabel="All Sites"
              />
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={buttonDisabled || !selectedOptions.sector || !selectedOptions.region}
            className={`primary-btn ${styles.primaryBtn}`}
          >
            Go <IconArrowRight />
          </Button>
        </div>
      </FormControl>
    </LocalizationProvider>
  );
};

export default memo(FilterData);
